import React, { useEffect, useRef } from 'react';

type Link = { id: string; label: string; icon: React.ReactNode };

const ICON_RPT = (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 17V9M15 17v-6M3 3h18v18H3z" />
  </svg>
);
const ICON_CLOCK = (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);
const ICON_CHART = (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 3v18h18" />
    <path d="M7 15l4-4 3 3 5-6" />
  </svg>
);
const ICON_VELA = (
  <svg viewBox="0 0 24 24" width="14" height="14">
    <path d="M4 3 L10 20 L14 20 L20 3 L16 3 L12 15 L8 3 Z" fill="currentColor" />
  </svg>
);

const REPORTS: Link[] = [
  { id: 'ar-aging',     label: 'A/R aging summary', icon: ICON_RPT },
  { id: 'ar-detail',    label: 'A/R aging detail',  icon: ICON_RPT },
  { id: 'sales-by-cust', label: 'Sales by customer', icon: ICON_CHART },
  { id: 'invoice-list', label: 'Invoice list export', icon: ICON_CLOCK },
];

const VELA_INSIGHTS: Link[] = [
  { id: 'who-pays-late', label: 'Who pays late',    icon: ICON_VELA },
  { id: 'best-day',      label: 'Best day to send', icon: ICON_VELA },
  { id: 'at-risk',       label: 'At risk invoices', icon: ICON_VELA },
];

const PANEL: React.CSSProperties = {
  position: 'absolute',
  top: 42,
  right: 0,
  minWidth: 240,
  background: '#FFFFFF',
  border: '1px solid #E7EAF0',
  borderRadius: 10,
  boxShadow: '0 10px 30px -8px rgba(14,26,26,.18)',
  padding: 10,
  zIndex: 20,
};

const HEAD: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: '#66748B',
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  padding: '6px 8px',
};

const ITEM: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: 13,
  color: '#0E1A1A',
  background: 'transparent',
  border: 0,
  borderRadius: 6,
  cursor: 'pointer',
};

const ITEM_VELA: React.CSSProperties = { ...ITEM, color: '#6B46C1' };

const DIVIDER: React.CSSProperties = { height: 1, background: '#F0F2F5', margin: '6px 0' };

type Props = {
  onClose: () => void;
  onSelect: (id: string) => void;
};

export default function QuickLinksMenu({ onClose, onSelect }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const pick = (id: string) => {
    onSelect(id);
    onClose();
  };

  return (
    <div ref={ref} style={PANEL} role="menu">
      <div style={HEAD}>Reports</div>
      {REPORTS.map((l) => (
        <button
          key={l.id}
          type="button"
          role="menuitem"
          style={ITEM}
          onMouseDown={(e) => { e.preventDefault(); pick(l.id); }}
          onMouseOver={(e) => (e.currentTarget.style.background = '#F7F8FA')}
          onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <span style={{ color: '#66748B' }}>{l.icon}</span>
          <span>{l.label}</span>
        </button>
      ))}
      <div style={DIVIDER} />
      <div style={HEAD}>Vela insights</div>
      {VELA_INSIGHTS.map((l) => (
        <button
          key={l.id}
          type="button"
          role="menuitem"
          style={ITEM_VELA}
          onMouseDown={(e) => { e.preventDefault(); pick(l.id); }}
          onMouseOver={(e) => (e.currentTarget.style.background = '#F5F3FF')}
          onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          {l.icon}
          <span>{l.label}</span>
        </button>
      ))}
    </div>
  );
}